import { userManagementApis } from "@services/apis/UserManagement";
import {
  GetNotificationsSettingsResponse,
  NotificationSettings,
  NotificationSettingsData,
} from "@services/models/UserManagement/get-notification-settings";
import { UpdateNotificationSettingRequest } from "@services/models/UserManagement/update-notification-settings";
import React from "react";
import useSnackbarMessage from "../../../../utils/HandleSuccessErrorSnackbars";
import NotificationsTableRow from "./NotificationTableRow";

interface NotificationAccountProps {
  userId: number;
  settingsData: NotificationSettingsData[] | undefined;
  loading: boolean;
  setPersonalSettings: React.Dispatch<
    React.SetStateAction<GetNotificationsSettingsResponse | null>
  >;
}

const NotificationsAccount: React.FC<NotificationAccountProps> = ({
  userId,
  settingsData,
  loading,
  setPersonalSettings,
}) => {
  const { handleError } = useSnackbarMessage();

  const handleEmailChange = async (
    index: number,
    notificationFrontEndKey: string,
  ) => {
    const emailValue = !(
      settingsData![index].notificationValue as NotificationSettings
    ).email.value;
    const telegramValue = (
      settingsData![index].notificationValue as NotificationSettings
    ).telegram.value;

    const data: UpdateNotificationSettingRequest = {
      userId,
      notificationFrontEndKey,
      notificationValue: {
        email: { value: emailValue },
        telegram: { value: telegramValue },
      },
    };

    try {
      await userManagementApis.updateNotificationSettings(data);
      setPersonalSettings((prevSettings) => {
        if (!prevSettings) return null;
        const updatedSettingsData = prevSettings.data.map((setting) => {
          if (setting.frontEndKey === notificationFrontEndKey) {
            // Update the relevant setting
            return {
              ...setting,
              notificationValue: {
                email: {
                  value: emailValue,
                },
                telegram: {
                  value: telegramValue,
                },
              },
            };
          }
          return setting;
        });
        return {
          ...prevSettings,
          data: updatedSettingsData,
        };
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "An unknown error occurred";
      handleError({ message: errorMessage });
    }
  };

  return (
    <div className="flex flex-col bg-bg-page-body px-[16px] pb-[16px]">
      <div className="flex flex-col mt-[16px]">
        <table className="min-w-full text-text-base text-[12px]">
          <thead className="bg-bg-header rounded-t-lg">
            <tr>
              <th
                className="p-[16px] text-left text-text-base text-[14px] rounded-tl-lg font-semibold"
                style={{ width: "300px" }}>
                Notify me about
              </th>
              <th className="p-[16px] text-center text-text-base text-[14px] rounded-tr-lg font-semibold">
                Email
              </th>
            </tr>
          </thead>
          <tbody className="min-w-full border border-border-table rounded-b-lg">
            {settingsData
              ? settingsData.map((setting, index) => {
                  const settingValue =
                    setting.notificationValue as NotificationSettings;
                  return (
                    <NotificationsTableRow
                      key={index}
                      title={setting.title}
                      email={settingValue.email.value}
                      rounded={index === settingsData.length - 1}
                      onEmailChange={() =>
                        handleEmailChange(index, setting.frontEndKey)
                      }
                      isEmailEnabled={true}
                      loading={loading}
                    />
                  );
                })
              : [...Array(2)].map((_, index) => (
                  <NotificationsTableRow
                    key={index}
                    title=""
                    email={false}
                    rounded={index === 4}
                    isEmailEnabled={false}
                    onEmailChange={() => {}}
                    loading={true}
                  />
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NotificationsAccount;
